<template>
  <div class="card">
    <div class="card-content">
      <div class="card-title-container">
        <div class="title-container">
          <div class="title is-5">{{ title }}</div>
          <div
            class="webhook-status"
            :class="{
              active: webhook.is_activated,
              error: hasError,
            }"
            v-if="webhook"
          >
            <i class="material-icons">fiber_manual_record</i>
          </div>
          <div class="tooltip-container">
            <i class="material-icons">&#xE88F;</i>
            <div class="tooltip tooltip-right" role="tooltip">
              <div class="tooltip-arrow"></div>
              <div class="tooltip-inner" v-if="type === 'requested'">
                {{ $t('webhooks.tooltips.requested_webhook_info') }}
              </div>
              <div class="tooltip-inner" v-else-if="type === 'active'">
                {{ $t('webhooks.tooltips.active_webhook_info') }}
              </div>
            </div>
          </div>
        </div>
        <div class="header-button-container" v-if="webhook">
          <div
            class="enable-disable-container"
            v-if="type === 'active' && webhook.is_deleted === false && !isOnlySpectator"
          >
            <button
              v-if="webhook.is_enabled === false"
              class="button btn-icon enable-btn"
              @click="openConfirmationModal('enable')"
              :title="$t('webhooks.tooltips.enable')"
            >
              <i class="mdi mdi-toggle-switch"></i>
            </button>
            <button
              v-else-if="webhook.is_enabled === true"
              class="button btn-icon disable-btn"
              @click="openConfirmationModal('disable')"
              :title="$t('webhooks.tooltips.disable')"
            >
              <i class="mdi mdi-toggle-switch-off"> </i>
            </button>
            <span v-else-if="webhook.auxillary_info" class="state-tag">
              {{
                webhook.auxillary_info.intermediate_state === 'awaiting_enable'
                  ? $t('webhooks.card.awaiting_enable')
                  : $t('webhooks.card.awaiting_disable')
              }}
            </span>
          </div>
          <div class="delete-btn-container">
            <button
              class="button btn-icon delete-btn"
              v-if="webhook.is_deleted === false && !isOnlySpectator"
              @click="openConfirmationModal('delete')"
              :title="$t('webhooks.tooltips.delete')"
            >
              <i class="mdi mdi-delete"> </i>
            </button>
            <span v-else-if="webhook.is_deleted === null" class="state-tag">
              {{ $t('webhooks.card.awaiting_deletion') }}
            </span>
          </div>
          <div class="webhook-details-btn-container">
            <button
              class="webhook-details-btn button btn-icon"
              @click="$emit('show-details', webhook)"
            >
              <i class="mdi mdi-unfold-more-horizontal"></i>
            </button>
          </div>
        </div>
      </div>
      <div>
        <div class="webhook-created-info">Created On
            <div class="tooltip-container">
            {{getFormattedDate(webhook.created_at)}}
            <div class="tooltip tooltip-right" role="tooltip">
              <div class="tooltip-inner">
                {{webhook.created_at}}
              </div>
            </div>
          </div>
        </div>
        <div class="webhook-created-info">By {{webhook.created_by}}</div>
      </div>
      <div class="card-body" :class="{ 'is-disabled': isDisabled }">
        <div v-if="isDisabled" class="overlay"></div>
        <div class="non-empty-webhook" v-if="webhook">
          <div
            v-if="webhook.is_auth_verified === true"
            class="verification-result"
          >
            <i class="material-icons">check_circle</i>
            <span>{{ $t('webhooks.card.auth_verified') }}</span>
          </div>
          <div
            v-else-if="webhook.is_auth_verified === false"
            class="verification-result error"
          >
            <i class="material-icons">error</i>
            <span>{{ $t('webhooks.card.auth_error') }}</span>
          </div>
          <div v-else class="verification-result waiting">
            <i class="material-icons">schedule</i>
            <span>{{ $t('webhooks.card.awaiting_auth_verification') }}</span>
          </div>
          <div
            v-if="webhook.is_app_id_verified === true"
            class="verification-result"
          >
            <i class="material-icons">check_circle</i>
            <span>{{ $t('webhooks.card.app_id_verified') }}</span>
          </div>
          <div
            v-else-if="webhook.is_app_id_verified === false"
            class="verification-result error"
          >
            <i class="material-icons">error</i>
            <span>{{ $t('webhooks.card.app_id_error') }}</span>
          </div>
          <div v-else class="verification-result waiting">
            <i class="material-icons">schedule</i>
            <span>
              {{ $t('webhooks.card.awaiting_app_id_verification') }}
            </span>
          </div>
          <div
            v-if="webhook.is_message_delivery_verified === true"
            class="verification-result"
          >
            <i class="material-icons">check_circle</i>
            <span>{{ $t('webhooks.card.delivery_verified') }}</span>
          </div>
          <div
            v-else-if="webhook.is_message_delivery_verified === false"
            class="verification-result error"
          >
            <i class="material-icons">error</i>
            <span>{{ $t('webhooks.card.delivery_error') }}</span>
          </div>
          <div v-else class="verification-result waiting">
            <i class="material-icons">schedule</i>
            <span>{{
              $t('webhooks.card.awaiting_delivery_verification')
            }}</span>
          </div>
          <div
            v-if="webhook.is_ssl_verified === true"
            class="verification-result"
          >
            <i class="material-icons">check_circle</i>
            <span>{{ $t('webhooks.card.ssl_verified') }}</span>
          </div>
          <div
            v-else-if="webhook.is_ssl_verified === false"
            class="verification-result error"
          >
            <i class="material-icons">error</i>
            <span>{{ $t('webhooks.card.ssl_error') }}</span>
          </div>
          <div v-else class="verification-result waiting">
            <i class="material-icons">schedule</i>
            <span>{{ $t('webhooks.card.awaiting_ssl_verification') }}</span>
          </div>
        </div>
        <div class="empty-webhook" v-else>
          {{ $t('webhooks.card.no_webhook') }}
        </div>
        <div v-if="webhook">
          <div class="validation-status has-text-centered">
            <div
              v-if="validationStatus[type] === 'in_progress'"
              class="in-progress"
            >
              {{ $t('webhooks.card.verification_in_progress') }}
            </div>
            <div
              v-else-if="validationStatus[type] === 'success'"
              class="success"
            >
              {{ $t('webhooks.card.verification_success') }}
            </div>
            <div v-else class="error">
              {{ $t('webhooks.card.verification_error') }}
            </div>
          </div>
          <div
            class="validation-error-container"
            v-if="validationErrors.length && type === 'requested'"
          >
            <h5 class="error-title">{{ $t('webhooks.card.errors') }}</h5>
            <div v-for="action in validationErrors" :key="action.id">
              <div v-if="action.data">{{ action.data }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      class="card-footer"
      :class="{ 'is-disabled': isDisabled }"
      v-if="
        !(type === 'requested' && validationStatus.requested === 'in_progress')
      "
    >
      <div v-if="isDisabled" class="overlay"></div>
      <div
        class="retry-verification is-flex"
        v-if="validationErrors.length && type === 'requested' && !isOnlySpectator"
      >
        <span>{{ $t('webhooks.card.have_you_fixed_error') }}</span>
        <button class="button primary retry-btn" @click="retryWebhookRequest">
          {{ $t('webhooks.card.retry') }}
        </button>
      </div>
      <div class="approval-status is-flex" v-else-if="webhook">
        <div v-if="webhook.is_approved === true" class="success">
          {{ $t('webhooks.card.approved_by_admin') }} on
          <div class="tooltip-container">
            {{getFormattedDate(webhook.approved_at)}}
            <div class="tooltip tooltip-right" role="tooltip">
              <div class="tooltip-inner">
                {{webhook.approved_at}}
              </div>
            </div>
          </div>
        </div>
        <div v-else-if="webhook.is_approved === false" class="error">
          {{ $t('webhooks.card.rejected_by_admin') }}
          <br />
          {{ rejectionReason.data.message }}
        </div>
        <div v-else class="waiting">
          {{ $t('webhooks.card.awaiting_approval') }}
        </div>
      </div>
      <div class="how-to-webhook is-flex" v-else>
        <a
          class="how-to-webhook-link"
          href="https://docs.sentiance.com/guide/firehose"
          target="_blank"
        >
          {{ $t('webhooks.card.how_to_webhook') }}
          <span class="icon is-small">
            <i class="material-icons">arrow_forward</i>
          </span>
        </a>
      </div>
    </div>
    <ConfirmationModal
      :type="confirmationModal.type"
      :is-active="confirmationModal.isActive"
      @confirmation-received="onConfirmation"
      @close-modal="closeConfirmationModal"
    />
  </div>
</template>

<script>
import * as moment from 'moment';
import { mapGetters } from 'vuex';

import Api from 'services/Api';
import notifications from 'services/Notifications';
import ConfirmationModal from 'views/boards/webhooks/modules/ConfirmationModal';
import { isStatus2xx } from 'utils/http';

export default {
  name: 'WebhookCard',
  props: {
    app: {
      type: Object,
      required: true,
    },
    isOnlySpectator: {
      type: Boolean,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      default: '',
    },
    webhook: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      confirmationModal: {
        isActive: false,
        type: '',
      },
    };
  },

  components: {
    ConfirmationModal,
  },

  methods: {
    getFormattedDate(date) {
      return moment(date).format('Do MMM, YYYY');
    },

    onConfirmation() {
      const actionMap = {
        delete: 'initiate_deletion',
        enable: 'initiate_enabling',
        disable: 'initiate_disabling',
      };
      const action = this.confirmationModal.type;

      if (actionMap[action]) {
        this.updateWebhookRequest(actionMap[action]);
      }

      this.closeConfirmationModal();
    },

    openConfirmationModal(type) {
      this.confirmationModal = {
        isActive: true,
        type,
      };
    },

    closeConfirmationModal() {
      this.confirmationModal = {
        isActive: false,
        type: '',
      };
    },

    async retryWebhookRequest() {
      try {
        const response = await Api.ganderPortal.put(
          `apps/${this.app.id}/webhook_requests/${this.webhook.id}/retry`
        );

        if (isStatus2xx(response.status)) {
          this.$emit('request-success');
        }
      } catch (err) {
        console.error(err);
        notifications.add(this.$t('webhooks.errors.retry_failed'));
      }
    },

    async updateWebhookRequest(action) {
      try {
        const response = await Api.ganderPortal.patch(
          `apps/${this.app.id}/webhook_requests/${this.webhook.id}`,
          {
            action,
          }
        );

        if (isStatus2xx(response.status)) {
          this.$emit('update-success');
        }
      } catch (err) {
        console.error(err);
        notifications.add(this.$t('webhooks.errors.getting_status_failed'));
      }
    },
  },
  computed: {
    ...mapGetters('webhook', [
      'validationStatus',
      'validationErrors',
      'rejectionReason',
    ]),
    hasError() {
      return [
        'is_auth_verified',
        'is_app_id_verified',
        'is_message_delivery_verified',
        'is_ssl_verified',
        'is_approved',
      ].some(key => {
        if (this.webhook[key] === false) {
          return true;
        }
        return false;
      });
    },
    isDisabled() {
      return (
        this.webhook &&
        (this.webhook.is_enabled === false ||
          (this.webhook.is_enabled === null &&
            this.webhook.auxillary_info.intermediate_state ===
              'awaiting_enable'))
      );
    },
  },
};
</script>
